import { FeesRepo } from "@services/fees.repo";
import { ClientRepo } from "@services/clients/client.repo";
import * as moment from "moment";
import { extractErrorMessage } from "@common/utils/errorUtils";

class FeesRecalculationController implements angular.IOnInit {
  // Internal state
  public isRecalculatingDaily: boolean = false;
  public recalcClientNumber: string = "";
  public searchText: string = "";
  public recalceFromDate: Date = new Date('2019-11-01');
  public recalceToDate: Date = new Date('2019-11-30');
  public IndividualClientsCurrency: string = "MUR";
  public isRecalculating: boolean = false;
  public recalcResults: any = null;

  // Bindings from parent
  public isLocalOrUATEnvironment: boolean;
  public clientsForUser: Array<any>;
  public currencyList = ["MUR", "EUR", "GBP", "USD", "CHF"];
  public lastMonth: Date;
  public onRecalculationComplete: () => void;

  /** @ngInject */
  constructor(
    private feesRepo: FeesRepo,
    private clientRepo: ClientRepo,
    private notifyjs: any,
  ) {}

  $onInit() {
    this.populateClients();
  }

  formatDate(selectedDate) {
    return moment(selectedDate).endOf("month").format("YYYY-MM");
  }

  recalculateIndividualFees = async () => {
    if (!this.recalcClientNumber || !this.recalceFromDate) {
      this.notifyjs("Please enter both client number and calculation date");
      return;
    }

    let fromDate = moment(this.recalceFromDate);
    let toDate = this.recalceToDate ? moment(this.recalceToDate) : fromDate;

    if (!this.isRecalculatingDaily) {
      toDate = moment(this.recalceToDate).endOf("month");
      fromDate = moment(this.recalceFromDate).startOf("month");
    }

    this.isRecalculating = true;
    this.recalcResults = null;

    try {
      // Determine the date range
      const startDate = fromDate;
      const endDate = toDate;

      // Generate array of dates to process
      const datesToProcess = [];
      let currentDate = startDate.clone();

      while (currentDate.isSameOrBefore(endDate, 'day')) {
        datesToProcess.push(currentDate.format("YYYY-MM-DD"));
        currentDate.add(1, 'day');
      }

      this.notifyjs(`Starting fee recalculation for ${datesToProcess.length} date(s) for client ${this.recalcClientNumber}`);

      // Process each date sequentially
      const results = [];
      for (let i = 0; i < datesToProcess.length; i++) {
        const date = datesToProcess[i];
        try {
          this.notifyjs(`Processing date ${i + 1}/${datesToProcess.length}: ${date}`);
          const res = await this.feesRepo.calculateFees(this.recalcClientNumber, date, this.isRecalculatingDaily);
          results.push({ date, success: true, data: res.data });
        } catch (err) {
          results.push({
            date,
            success: false,
            error: err.data || err.message || "Failed to recalculate fees"
          });
          // Continue processing other dates even if one fails
        }
      }

      // Analyze results
      const successfulDates = results.filter(r => r.success);
      const failedDates = results.filter(r => !r.success);

      if (failedDates.length === 0) {
        this.recalcResults = {
          success: true,
          message: `Fees recalculated successfully for all ${successfulDates.length} date(s)`,
          data: results
        };
        this.notifyjs(`Fees recalculated successfully for all ${successfulDates.length} date(s) for client ${this.recalcClientNumber}`);
      } else if (successfulDates.length > 0) {
        this.recalcResults = {
          success: false,
          message: `Partial success: ${successfulDates.length} date(s) succeeded, ${failedDates.length} date(s) failed`,
          data: results
        };
        this.notifyjs(`Partial success: ${successfulDates.length} date(s) succeeded, ${failedDates.length} date(s) failed for client ${this.recalcClientNumber}`);
      } else {
        this.recalcResults = {
          success: false,
          message: `All ${failedDates.length} date(s) failed to recalculate`,
          data: results
        };
        this.notifyjs(`All ${failedDates.length} date(s) failed to recalculate for client ${this.recalcClientNumber}`);
      }

      // Notify parent to refresh data
      if (this.onRecalculationComplete) {
        this.onRecalculationComplete();
      }

    } catch (err) {
      this.recalcResults = {
        success: false,
        message: err.data || "Failed to recalculate fees"
      };
      this.notifyjs("Failed to recalculate fees: " + extractErrorMessage(err));
    } finally {
      this.isRecalculating = false;
    }
  };

  recalulateIndividualsFinalMonthFees = async () => {
    if (!this.recalcClientNumber || !this.recalceFromDate) {
      this.notifyjs("Please enter both client number and calculation date");
      return;
    }


    let fromDate = moment(this.recalceFromDate);
    let toDate = this.recalceToDate ? moment(this.recalceToDate).endOf("month").format("YYYY-MM-DD") : fromDate.endOf("month").format("YYYY-MM-DD");

    this.isRecalculating = true;

    const results = [];
    try {
      this.notifyjs(`Processing date: ${toDate}`);
      const res = await this.feesRepo.calculateFees(this.recalcClientNumber, toDate, false);
      results.push({ toDate, success: true, data: res.data });
    } catch (err) {
      results.push({
        toDate,
        success: false,
        error: err.data || err.message || "Failed to recalculate fees"
      });
    }

    // Analyze results
    const successfulDates = results.filter(r => r.success);
    const failedDates = results.filter(r => !r.success);

    if (failedDates.length === 0) {
      this.recalcResults = {
        success: true,
        message: `Fees recalculated successfully for all ${successfulDates.length} date(s)`,
        data: results
      };
      this.notifyjs(`Fees recalculated successfully for all ${successfulDates.length} date(s) for client ${this.recalcClientNumber}`);
    } else if (successfulDates.length > 0) {
      this.recalcResults = {
        success: false,
        message: `Partial success: ${successfulDates.length} date(s) succeeded, ${failedDates.length} date(s) failed`,
        data: results
      };
      this.notifyjs(`Partial success: ${successfulDates.length} date(s) succeeded, ${failedDates.length} date(s) failed for client ${this.recalcClientNumber}`);
    } else {
      this.recalcResults = {
        success: false,
        message: `All ${failedDates.length} date(s) failed to recalculate`,
        data: results
      };
      this.notifyjs(`All ${failedDates.length} date(s) failed to recalculate for client ${this.recalcClientNumber}`);
    }

    this.isRecalculating = false;
  }

  recalculateAllDailyFees = async () => {
    if (!this.recalceFromDate) {
      this.notifyjs("Please enter a calculation date");
      return;
    }

    let fromDate = moment(this.recalceFromDate);
    let toDate = this.recalceToDate ? moment(this.recalceToDate) : fromDate;

    if (!this.isRecalculatingDaily) {
      toDate = moment(this.recalceToDate).endOf("month");
      fromDate = moment(this.recalceFromDate).startOf("month");
    }

    this.isRecalculating = true;
    this.recalcResults = null;

    try {
      // Determine the date range
      const startDate = fromDate;
      const endDate = fromDate;

      // Generate array of dates to process
      const datesToProcess = [];
      let currentDate = startDate.clone();

      while (currentDate.isSameOrBefore(endDate, 'day')) {
        datesToProcess.push(currentDate.format("YYYY-MM-DD"));
        currentDate.add(1, 'day');
      }

      this.notifyjs(`Starting fee recalculation for ${datesToProcess.length} date(s) for client ${this.recalcClientNumber}`);

      // Process each date sequentially
      const results = [];
      for (let i = 0; i < datesToProcess.length; i++) {
        const date = datesToProcess[i];
        try {
          this.notifyjs(`Processing date ${i + 1}/${datesToProcess.length}: ${date}`);
          const res = await this.feesRepo.calculateAllDailyFees(date);
          console.log("successfully recalculated fees for date: " + date);
          console.log(res);
          results.push({ date, success: true, data: res.data });
        } catch (err) {
          console.log("failed to recalculate fees for date: " + date);
          console.log(err);
          results.push({
            date,
            success: false,
            error: err.data || err.message || "Failed to recalculate fees"
          });
          // Continue processing other dates even if one fails
        }
      }

      // Analyze results
      const successfulDates = results.filter(r => r.success);
      const failedDates = results.filter(r => !r.success);

      if (failedDates.length === 0) {
        this.recalcResults = {
          success: true,
          message: `Fees recalculated successfully for all ${successfulDates.length} date(s)`,
          data: results
        };
        this.notifyjs(`Fees recalculated successfully for all ${successfulDates.length} date(s) for client ${this.recalcClientNumber}`);
      } else if (successfulDates.length > 0) {
        this.recalcResults = {
          success: false,
          message: `Partial success: ${successfulDates.length} date(s) succeeded, ${failedDates.length} date(s) failed`,
          data: results
        };
        this.notifyjs(`Partial success: ${successfulDates.length} date(s) succeeded, ${failedDates.length} date(s) failed for client ${this.recalcClientNumber}`);
      } else {
        this.recalcResults = {
          success: false,
          message: `All ${failedDates.length} date(s) failed to recalculate`,
          data: results
        };
        this.notifyjs(`All ${failedDates.length} date(s) failed to recalculate for client ${this.recalcClientNumber}`);
      }

      // Notify parent to refresh data
      if (this.onRecalculationComplete) {
        this.onRecalculationComplete();
      }

    } catch (err) {
      this.recalcResults = {
        success: false,
        message: err.data || "Failed to recalculate fees"
      };
      this.notifyjs("Failed to recalculate fees: " + extractErrorMessage(err));
    } finally {
      this.isRecalculating = false;
    }
  }

  recalculateAllMonthlyFees = async () => {
    if (!this.recalceToDate) {
      this.notifyjs("Please select a month");
      return;
    }

    this.isRecalculating = true;
    this.recalcResults = null;

    try {
      // Get the last day of the selected month
      const lastDayOfMonth = moment(this.recalceToDate).endOf("month").format("YYYY-MM-DD");

      this.notifyjs(`Starting monthly fee recalculation for ${lastDayOfMonth}`);

      const res = await this.feesRepo.calculateAllEOMFees(lastDayOfMonth);

      this.recalcResults = {
        success: true,
        message: `Monthly fees recalculated successfully for ${lastDayOfMonth}`,
        data: res.data
      };

      this.notifyjs(`Monthly fees recalculated successfully for ${lastDayOfMonth}`);

      // Notify parent to refresh data
      if (this.onRecalculationComplete) {
        this.onRecalculationComplete();
      }

    } catch (err) {
      this.recalcResults = {
        success: false,
        message: err.data || "Failed to recalculate monthly fees"
      };
      this.notifyjs("Failed to recalculate monthly fees: " + extractErrorMessage(err));
    } finally {
      this.isRecalculating = false;
    }
  }

  public isLoadingClients: boolean = false;

  private populateClients() {
    this.isLoadingClients = true;

    this.clientRepo.getClientsForSelection()
      .then((res) => {
        // Map the selection endpoint response to match the expected structure
        const clientSelections = res.data as Array<{ id: number; clientNumber: string; displayName: string }>;
        this.clientsForUser = clientSelections.map(c => {
          return { name: c.displayName, clientNumber: c.clientNumber };
        });

        // Selection endpoint already returns sorted by DisplayName, but keeping sort for consistency
        this.clientsForUser.sort((a, b) => {
          if (a.name < b.name) return -1;
          if (a.name > b.name) return 1;
          return 0;
        });
      })
      .catch((err) => this.notifyjs("Failed to load clients: " + extractErrorMessage(err)))
      .finally(() => {
        this.isLoadingClients = false;
      });
  }
}

export const feesRecalculation: angular.IComponentOptions = {
  template: require("./fees-recalculation.html"),
  controller: FeesRecalculationController,
  bindings: {
    isLocalOrUATEnvironment: "<",
    clientsForUser: "<",
    lastMonth: "<",
    onRecalculationComplete: "&",
  },
};




// WEBPACK FOOTER //
// ./src/app/fees/fees-components/fees-recalculation/fees-recalculation.ts